import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { BackButton } from "@/common/BackButton";
import { Box } from "@mui/material";
import { useDispatch } from "react-redux";
import {
  CustomSectionTitle,
  CustomLoading,
} from "@ippon/shared-components";
import type { AppDispatch } from "@/store";
import {
  fetchManagementTaskById,
  updateManagementTask,
  type ManagementTaskRecord,
  type UpdateManagementTaskBody,
} from "@/store/managementTaskSlice";
import ManagementTaskEditModal from "@/components/PropertyDetail/InspectionCleaning/drawers/ManagementTaskEditModal";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";
import type { ApiErrorPayload, ApiErrorType } from "@/utils/apiErrorPayload";

interface Props {
  /** PreviewDrawer 等で埋め込む際に使用する task_id (URL params より優先) */
  overrideTaskId?: string;
  /** PreviewDrawer 経由の property_id 指定 (querystring より優先) */
  overridePropertyId?: string;
  embedded?: boolean;
  /** 編集成功後コールバック (親で一覧再取得等) */
  onUpdated?: (taskId: string) => void;
  /** ロード成功時に呼ばれる (ドロワー埋め込み時にパンくず title 取得に使う) */
  onLoaded?: (task: ManagementTaskRecord) => void;
}

export const ManagementTaskDetailPage: React.FC<Props> = ({
  overrideTaskId,
  overridePropertyId,
  embedded,
  onUpdated,
  onLoaded,
}) => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();

  const taskId = overrideTaskId || params?.id || "";
  const propertyId =
    overridePropertyId || searchParams.get("property_id") || "";

  const [data, setData] = useState<ManagementTaskRecord | null>(null);
  const dataRef = useRef<ManagementTaskRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoadError, setInitialLoadError] =
    useState<ApiErrorType | null>(null);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // onLoaded を ref 経由で呼び、fetchDetail useCallback の deps から外す。
  // 親がインライン関数で渡してくると毎レンダー new ref になり fetch ループになるため。
  const onLoadedRef = useRef(onLoaded);
  useEffect(() => {
    onLoadedRef.current = onLoaded;
  }, [onLoaded]);

  const fetchDetail = useCallback(async () => {
    setInitialLoadError(null);
    if (!taskId || !propertyId) {
      // URL から property_id が引けない (直リン等) → notFound 扱い
      setInitialLoadError("notFound");
      return;
    }
    setLoading(true);
    try {
      const rec = await dispatch(
        fetchManagementTaskById({ property_id: propertyId, task_id: taskId }),
      ).unwrap();
      setData(rec);
      onLoadedRef.current?.(rec);
    } catch (err) {
      const payload = err as ApiErrorPayload | undefined;
      const errorType = payload?.errorType ?? "unknown";
      setInitialLoadError(errorType);
      if (errorType === "notFound" || !dataRef.current) {
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, taskId, propertyId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // パンくず最終項目に業務名を反映
  const lastBreadcrumbTitleRef = useRef<string>("");
  useEffect(() => {
    if (embedded) return;
    const title = data?.name as string | undefined;
    if (!title) return;
    if (title === lastBreadcrumbTitleRef.current) return;
    lastBreadcrumbTitleRef.current = title;
    const currentState = (location.state as Record<string, unknown>) || {};
    navigate(`/management-tasks/${taskId}${location.search || ""}`, {
      state: { ...currentState, title },
      replace: true,
    });
  }, [data?.name, taskId, embedded, navigate, location.state, location.search]);

  const handleSaveExisting = async (
    task_id: string,
    body: UpdateManagementTaskBody,
  ) => {
    try {
      const updated = await dispatch(
        updateManagementTask({ property_id: propertyId, task_id, body }),
      ).unwrap();
      setData(updated);
      showToast({ type: "success", message: "管理業務を更新しました" });
      onUpdated?.(task_id);
    } catch (e) {
      const msg = typeof e === "string" ? e : "管理業務の更新に失敗しました";
      showToast({ type: "error", message: msg });
      throw e;
    }
  };

  const hasData = Boolean(data);
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasData,
    dedupeKey: `management-task:${taskId}`,
  });

  if (shouldShowInitialLoadError(initialLoadError, hasData)) {
    return (
      <InitialLoadError errorType={initialLoadError} onRetry={fetchDetail} />
    );
  }

  if (loading || !data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
          minHeight: "60vh",
          width: "100%",
        }}
      >
        <CustomLoading loading={true} fullScreen={false} size="medium" />
      </Box>
    );
  }

  return (
    <Box pb={9} pt={0} px={embedded ? 1.5 : 0}>
      <Box
        sx={{
          mb: 3,
          // タイトルを上端に sticky 固定。ドロワー(embedded)はスクロール先頭(0)、
          // 単独ページ(全画面)は固定ヘッダー(50px)の下に貼り付ける。
          // 上余白は sticky 要素の内側 pt で持たせる (外側だとスクロール時に動く)。
          pt: embedded ? 0 : 0.5,
          position: "sticky",
          top: embedded ? 0 : 50,
          zIndex: 10,
          backgroundColor: "white",
        }}
      >
        <CustomSectionTitle
          title={data.name || "管理業務"}
          size="large"
          addBorder
          icon={!embedded ? <BackButton size={24} /> : undefined}
        />
      </Box>

      <ManagementTaskEditModal
        open
        onClose={() => {
          // ページモードでは閉じるアクションは戻る相当。embedded なら親が drawer を
          // 閉じる責務を持つので何もしない。フルページ時はブラウザ戻るで遷移。
          if (!embedded) navigate(-1);
        }}
        task={data}
        property_id={propertyId}
        displayMode="page"
        embedded={embedded}
        onSaveExisting={handleSaveExisting}
        onSaveNew={async () => {}}
      />
    </Box>
  );
};

export default ManagementTaskDetailPage;
